import { z } from "zod";

// ── REGRAS RECORRENTES ────────────────────────────────────────────────────────────────────────
// Arquivo próprio, e não um bloco em `operating-calendar.ts`, porque aquele arquivo cruzou o teto
// de 200 linhas efetivas ao recebê-lo (guardrail #1, pego pelo hook de pre-commit). A fronteira é
// natural: uma regra recorrente é declarada independentemente de qualquer calendário — o calendário
// da linha e o da organização apenas guardam listas dela.

export const MAX_RECURRING_EXCEPTIONS = 100;

/** Último = -1. Num mês sem 5ª ocorrência, resolve para a 4ª — sem caso especial (research). */
export const MONTHLY_ORDINALS = [1, 2, 3, 4, -1] as const;

const recurrenceCommonFields = {
  kind: z.enum(["CLOSED", "EXTRA_WINDOW"]),
  // Mesmas faixas de WeeklyShift, incluindo a convenção de travessia da meia-noite (endMinute
  // até 2879). Opcionais no schema porque só `EXTRA_WINDOW` os exige — o superRefine abaixo é
  // quem torna a exigência real, ancorando a issue no campo que faltou.
  startMinute: z.number().int().min(0).max(1439).optional(),
  endMinute: z.number().int().min(1).max(2879).optional(),
  reason: z.string().trim().max(200).optional(),
};

/** Maior dia possível de cada mês (fevereiro com 29 — ano bissexto é ocorrência legítima). */
const MAX_DAY_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

type RecurrenceDraft = {
  freq: "YEARLY" | "MONTHLY" | "WEEKLY";
  kind: "CLOSED" | "EXTRA_WINDOW";
  month?: number;
  day?: number;
  startMinute?: number;
  endMinute?: number;
};

function checkRecurrence(rule: RecurrenceDraft, ctx: z.RefinementCtx): void {
  // A issue de obrigatório condicional MUST apontar o campo que faltou. Um `path` fixo
  // faria `classifyArgIssues` classificar como `custom`, e o campo ausente deixaria de ser
  // nomeável por quem precisa pedi-lo de volta ao usuário.
  if (rule.kind === "EXTRA_WINDOW") {
    if (rule.startMinute === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Informe o início da janela extra",
        path: ["startMinute"],
      });
    }
    if (rule.endMinute === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Informe o fim da janela extra",
        path: ["endMinute"],
      });
    }
    if (
      rule.startMinute !== undefined &&
      rule.endMinute !== undefined &&
      rule.endMinute <= rule.startMinute
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "O fim da janela extra deve ser depois do início",
        path: ["endMinute"],
      });
    }
  }

  // 31 de abril nunca acontece. Uma regra assim não é "estéril no período" (que é legítimo e vira
  // no-op) — ela é estéril em TODO período possível, ou seja, dado morto. Recusar na escrita
  // custa uma linha e evita o gestor esperando por um feriado que nunca chega.
  if (rule.freq === "YEARLY" && rule.month !== undefined && rule.day !== undefined) {
    const max = MAX_DAY_IN_MONTH[rule.month - 1];
    if (max !== undefined && rule.day > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Data inexistente: o mês ${rule.month} não tem dia ${rule.day}`,
        path: ["day"],
      });
    }
  }
}

export const RecurringExceptionSchema = z
  .discriminatedUnion("freq", [
    z.object({
      freq: z.literal("YEARLY"),
      month: z.number().int().min(1).max(12),
      day: z.number().int().min(1).max(31),
      ...recurrenceCommonFields,
    }),
    z.object({
      freq: z.literal("MONTHLY"),
      weekday: z.number().int().min(0).max(6), // 0 = domingo, como WeeklyShift
      ordinal: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]),
      ...recurrenceCommonFields,
    }),
    z.object({
      freq: z.literal("WEEKLY"),
      weekday: z.number().int().min(0).max(6),
      ...recurrenceCommonFields,
    }),
  ])
  .superRefine(checkRecurrence);
export type RecurringException = z.infer<typeof RecurringExceptionSchema>;

export const RecurringExceptionsSchema = z
  .array(RecurringExceptionSchema)
  .max(MAX_RECURRING_EXCEPTIONS);

/**
 * NÃO há validação de sobreposição entre regras. Duas regras que incidem no mesmo dia são união
 * legítima, e `CLOSED` no mesmo dia que `EXTRA_WINDOW` é resolvido por PRECEDÊNCIA
 * (`mergeCalendarLayers`), não recusado na escrita. Recusar aqui obrigaria o gestor a antecipar
 * mentalmente a expansão de duas regras para saber se pode salvar.
 */
